<template>
    <div id="app">
        <message-queue></message-queue>

        <div v-if="this.infoMessage" id="bfr-info" class="w3-container w3-center w3-card">
            <md-viewer :initialValue="this.infoMessage"></md-viewer>
        </div>

        <div id="top-bar" class="theme-primary w3-display-container">
            <div id="apptitle" class="w3-display-left">
                <h1>
                    <router-link to="/">BfR Produktmitteilungen Online</router-link>
                    <span v-if="apiEnv === 'development'">&nbsp;// Dev</span>
                    <span v-else-if="apiEnv === 'test'">&nbsp;// Test</span>
                </h1>
                
            </div>
            <div id="profileoptions" class="w3-display-right">
                <profile-button :user="currentUser"></profile-button>
            </div>
        </div>
        <div id="navigation-bar">
            <router-link to="/chemg"
                    v-if="currentUser !== null && (currentUser.hasPrivilege('admin') || currentUser.hasPrivilege('chemg'))"
                    :class="[{'active': $route.path === '/chemg'}]">{{ $t('hazardousMixtures') }}
            </router-link>
            <router-link to="/wrmg"
                    v-if="currentUser !== null && (currentUser.hasPrivilege('admin') || currentUser.hasPrivilege('wrmg'))"
                    :class="[{'active': $route.path === '/wrmg'}]">{{ $t('washingAndCleaningSolutions') }}
            </router-link>
            <router-link to="/admin/users"
                    v-if="currentUser !== null && (currentUser.hasPrivilege('admin') || currentUser.hasPrivilege('manage-users'))"
                    :class="[{'active': $route.path === '/admin/users'}]">{{ $t('manageUsers') }}
            </router-link>
            <router-link to="/admin/employers"
                    v-if="currentUser !== null && (currentUser.hasPrivilege('admin') || currentUser.hasPrivilege('manage-employers'))"
                    :class="[{'active': $route.path === '/admin/employers'}]">{{ $t('Manage agencies') }}
            </router-link>
            <router-link to="/admin/faq"
                    v-if="currentUser !== null && currentUser.hasPrivilege('admin')"
                    :class="[{'active': $route.path === '/admin/faq'}]">{{ $t('manageFaq') }}
            </router-link>
            <router-link to="/admin/info"
                    v-if="currentUser !== null && currentUser.hasPrivilege('admin')"
                    :class="[{'active': $route.path === '/admin/info'}]">{{ $t('manageInfo') }}
            </router-link>

            <div class="w3-right mr-30">
                <router-link to="/faq"
                        :class="[{ 'active': $route.path.startsWith('/faq') }]"> Hilfe / FAQ</router-link>
            </div>
        </div>

        <div id="bfr-content" class="w3-container">
            <router-view :key="$route.fullPath" />
        </div>

        <div id="footer" class="w3-center">
            Version: {{appVersion}} | API: {{apiVersion}} | Datenstand: {{dataVersion}} <br>
            &copy; 2019-{{ $moment().format('YYYY') }} Bundesinstitut f&uuml;r Risikobewertung<br><br>
            <router-link to="/imprint">Impressum</router-link> &nbsp;
            <router-link to="/contact">Kontakt</router-link> &nbsp;
            <router-link to="/privacy">Datenschutz</router-link>
        </div>

        <div id="cookie-notice" v-if="cookieNoticeAccepted !== true">
            <h2>{{ $t('Cookie Notice') }}</h2>
            {{ $t('Cookie Notice::Description') }}&nbsp;
            <button class="w3-button w3-tiny theme-complement-l2 theme-complement-l2-hover"
                    @click="acceptCookieNotice()">{{ $t('OK') }}</button>
        </div>
    </div>
</template>

<style>
@import url("assets/css/w3.css");
@import url("assets/css/theme.css");

body {
    margin: 0;
}

#bfr-info {
    background-color: #bad8e9;
    color: #003869;
    font-size: smaller;
}

#top-bar {
    height: 60px;
}

#apptitle h1 {
    font-size: 1.5em;
    color: #fff;
    margin: 0;
    line-height: 1;
    display: inline-block;
    margin-right: 20px;
}
#apptitle a {
    margin-left: 20px;
    color: #fff;
    text-decoration: none;
}

#profileoptions {
    margin-right: 20px;
    z-index: 1;
}

#navigation-bar {
    border-bottom: 2px solid #003869;
    height: 45.5px;
}
#navigation-bar a {
    display: inline-block;
    padding: 10px;
    text-decoration: none;
    border-top: 1px solid #fff;
}
#navigation-bar a:hover,
#navigation-bar a.active {
    background: #003869;
    color: #fff;
    border-top: 1px solid #0467d6;
}

#bfr-content {
    margin-top: 20px;
}

#footer {
    border-top: 2px solid #003869;
    margin-top: 10px;
    margin-bottom: 20px;
    padding-top: 10px;
    font-size: .8em;
}

#cookie-notice {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    padding: 10px;
    background: #003869;
    color: #fff;
    font-size: .8em;
}

#cookie-notice h2 {
    font-size: 1.2em;
    font-weight: bold;
    margin: 0;
}

h1 {
    color: #003869;
    font-weight: bold;
}
</style>

<script>
import moment from 'moment'

import { Viewer } from '@toast-ui/vue-editor'

import Api from 'api-client'
import store from '@/store'
import EventBus from '@/util/EventBus'

import User from '@/model/User'

import ProfileButton from '@/components/ProfileButton'
import MessageQueue from '@/components/MessageQueue'

export default {
    components: {
        ProfileButton,
        MessageQueue,
        MdViewer: Viewer
    },

    computed: {
        currentUser: () => { return store.state.currentUser instanceof User ? store.state.currentUser : null },
        cookieNoticeAccepted: () => {
            const value = store.state.cookieNoticeAccepted
            return typeof value === 'boolean' ? value : false
        }
    },

    data () {
        return {
            appVersion: process.env.VUE_APP_VERSION,
            apiVersion: 'N/A',
            dataVersion: 'N/A',
            apiEnv: 'production',
            infoMessage: undefined
        }
    },

    beforeCreate () {
        var self = this

        // Register force-logout event sent by the API connectors if we get 401 UNAUTHORIZED responses, e.g. after the
        // session expires.
        EventBus.$on('force-logout', () => {
            self.infoMessage = undefined
            store.commit('setCurrentUser', null)
            self.$router.push('/login')
            window.toast({
                text: self.$i18n.t('errors.sessionInvalidOrExpired'),
                color: 'red'
            })
        })

        // Register logout event sent by the ProfileButton view.
        EventBus.$on('logout', () => {
            self.infoMessage = undefined
        })

        // Register login event sent by the Login view.
        EventBus.$on('login', () => {
            self.loadInfoMessage()
        })

        EventBus.$on('update-info-message', () => {
            self.loadInfoMessage()
        })
    },

    created () {
        const self = this

        Api.getInfoVersion()
            .then(resp => {
                if (resp.data == null || typeof resp.data.apiVersion !== 'string') {
                    throw new Error()
                }
                self.apiVersion = resp.data.apiVersion
                if (typeof resp.data.dataVersion === 'string') {
                    const dataVersion = moment(resp.data.dataVersion).format('DD.MM.YYYY, HH:mm')
                    self.dataVersion = dataVersion.toLocaleString();
                }

                if (typeof resp.data.environment === 'string' && ['development', 'test', 'production'].includes(resp.data.environment)) {
                    self.apiEnv = resp.data.environment
                }
            })
            .catch(() => {
                window.toast({
                    text: self.$i18n.t('The API seems to be unreachable - please try again later. If the problem persists, please contact support.'),
                    color: 'red',
                    timeout: 0
                })
            })
        
        // If a user profile is stored, re-load information from the API
        let user = store.state.currentUser
        if (user != null && typeof user === 'object' && !(user instanceof User)) {
            try {
                user = User.fromObject(user)
                store.commit('setCurrentUser', user)
                self.loadUserDetails(user.uuid)
            } catch (err) {
                EventBus.$emit('force-logout')
            }
        }
    },

    mounted () {
        this.loadAvailableStatesList()
    },

    methods: {
        loadAvailableStatesList () {
            const self = this

            Api.getAllStates()
                .then(resp => {
                    store.commit('availableStates', resp.data)
                })
                .catch(() => {
                    window.toast({
                        text: self.$i18n.t('Failed to load available states'),
                        color: 'red'
                    })
                })
        },

        acceptCookieNotice () {
            store.commit('cookieNoticeAccepted', true)
        },

        loadUserDetails (uuid) {
            const self = this

            Api.getUserDetails(uuid)
            .then(resp => {
                const user = User.fromObject(resp.data)
                self.loadEmployerInformation(user)
                self.loadInfoMessage()
            })
            .catch(() => {}) // Already handled in API, no additional handling required
        },

        loadEmployerInformation (user) {
            Api.getEmployer(user.employerUuid)
            .then(resp => {
                user.employer = resp.data
                store.commit('setCurrentUser', user)
            })
            .catch(() => {}) // Already handled in API, no additional handling required
        },

        loadInfoMessage () {
            this.infoMessage = undefined
            Api.getActiveInfoMessageText()
            .then(resp => {
                this.infoMessage = resp.data
            })
            .catch(() => {}) // Already handled in API, no additional handling required
        }
    }
}
</script>

<i18n src="@/lang/common.json"></i18n>
<i18n>
{
    "de": {
        "Failed to load available states": "Die verfügbaren Bundesländer konnten nicht abgerufen werden",
        "The API seems to be unreachable - please try again later. If the problem persists, please contact support.": "Die API scheint nicht erreichbar zu sein. Bitte versuchen Sie es später erneut. Besteht das Problem weiterhin, kontaktieren Sie bitte den Support."
    },
    "en": {
        "Failed to load available states": "Failed to load available states",
        "The API seems to be unreachable - please try again later. If the problem persists, please contact support.": "The API seems to be unreachable - please try again later. If the problem persists, please contact support."
    }
}
</i18n>
